import React, { useCallback, useMemo } from 'react';

import PropTypes from 'prop-types';
import cn from 'classnames';
import { Icon } from '@picsio/ui';
import { Warning } from '@picsio/icons';

import resolveSyncMediaSelectionState, {
  SYNC_MEDIA_SEVERITY,
} from '../../../../helpers/shopify/resolveSyncMediaSelectionState';
import Checkbox from '../../../../UIComponents/checkbox';
import Tooltip from '../../../Tooltip';
import getSyncMediaTileTooltip from '../helpers/getSyncMediaTileTooltip';
import styles from '../styles.module.scss';

function MediaThumbnailGrid({
  assets,
  selectedAssetIds,
  onChange,
  selectionMode,
  onAssetClick,
  tileSize,
  showCheckbox,
  gridClassName,
  tileClassName,
  showValidationStatus,
  requirePublishReady,
}) {
  const selectedSet = useMemo(
    () => new Set(selectedAssetIds.map(String)),
    [selectedAssetIds],
  );

  const handleToggle = useCallback((assetId) => {
    if (onAssetClick) {
      onAssetClick(assetId);
      return;
    }

    const normalizedAssetId = String(assetId);

    if (selectionMode === 'single') {
      onChange([normalizedAssetId]);
      return;
    }

    if (selectedSet.has(normalizedAssetId)) {
      onChange(selectedAssetIds.filter((id) => String(id) !== normalizedAssetId));
      return;
    }

    onChange([...selectedAssetIds, normalizedAssetId]);
  }, [onAssetClick, onChange, selectedAssetIds, selectedSet, selectionMode]);

  return (
    <div className={cn(styles.connectChannelSectionMediaGrid, {
      [styles.connectChannelSectionMediaGridLarge]: tileSize === 'large',
    }, gridClassName)}
    >
      {assets.map((asset) => {
        const assetId = String(asset._id);
        const isSelected = selectedSet.has(assetId);
        const thumbnailUrl = asset.thumbnail?.small || asset.thumbnail?.medium || asset.url;
        const filename = asset.name || asset.title || asset.fileName || 'Untitled';
        const selectionState = showValidationStatus
          ? resolveSyncMediaSelectionState(asset, { requirePublishReady })
          : null;
        const tooltipContent = showValidationStatus
          ? getSyncMediaTileTooltip(filename, selectionState)
          : filename;
        const showStatusBadge = selectionState
          && selectionState.severity !== SYNC_MEDIA_SEVERITY.OK;
        const isHard = selectionState?.severity === SYNC_MEDIA_SEVERITY.HARD;

        return (
          <Tooltip key={asset._id} content={tooltipContent} placement="top">
            <button
              type="button"
              className={cn(styles.connectChannelSectionMediaTile, {
                [styles.connectChannelSectionMediaTileLarge]: tileSize === 'large',
                [styles.connectChannelSectionMediaTileSelected]: isSelected,
              }, tileClassName)}
              onClick={() => handleToggle(asset._id)}
            >
              <If condition={thumbnailUrl}>
                <img
                  className={styles.connectChannelSectionMediaTileImage}
                  src={thumbnailUrl}
                  alt={filename}
                />
              </If>
              <If condition={!thumbnailUrl}>
                <span className={styles.connectChannelSectionMediaTileFilename}>
                  {filename}
                </span>
              </If>
              <If condition={showStatusBadge}>
                <span
                  className={styles.connectChannelSectionMediaTileBadge}
                  aria-hidden="true"
                >
                  <Icon size="md" color={isHard ? 'error' : 'warning'}>
                    <Warning />
                  </Icon>
                </span>
              </If>
              <If condition={showCheckbox}>
                <span
                  className={styles.connectChannelSectionMediaTileCheckbox}
                  role="presentation"
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <Checkbox value={isSelected} onChange={() => handleToggle(asset._id)} />
                </span>
              </If>
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
}

MediaThumbnailGrid.propTypes = {
  assets: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string.isRequired,
    name: PropTypes.string,
    title: PropTypes.string,
    fileName: PropTypes.string,
    url: PropTypes.string,
    thumbnail: PropTypes.shape({
      small: PropTypes.string,
      medium: PropTypes.string,
    }),
  })).isRequired,
  selectedAssetIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func,
  selectionMode: PropTypes.oneOf(['multi', 'single']),
  onAssetClick: PropTypes.func,
  tileSize: PropTypes.oneOf(['default', 'large']),
  showCheckbox: PropTypes.bool,
  gridClassName: PropTypes.string,
  tileClassName: PropTypes.string,
  showValidationStatus: PropTypes.bool,
  requirePublishReady: PropTypes.bool,
};

MediaThumbnailGrid.defaultProps = {
  onChange: undefined,
  selectionMode: 'multi',
  onAssetClick: undefined,
  tileSize: 'default',
  showCheckbox: true,
  gridClassName: undefined,
  tileClassName: undefined,
  showValidationStatus: true,
  requirePublishReady: false,
};

export default MediaThumbnailGrid;
